import React, { useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, ShoppingCart, Crown } from 'lucide-react';

interface CreditValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  requiredCredits: number;
  availableCredits: number;
  // Additional UI props passed from UploadPage
  filesCount?: number;
  fileBreakdown?: any;
  isValidating?: boolean;
  creditValidationResult?: any;
  uploadStatus?: any;
  albumCreationResult?: any;
  // New props for membership validation
  hasActiveMembership?: boolean;
  isFreeAccount?: boolean;
  membershipExpired?: boolean;
  currentPackage?: string;
  membershipExpiresAt?: string;
}

const CreditValidationModal: React.FC<CreditValidationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  requiredCredits,
  availableCredits,
  // New props for membership validation
  hasActiveMembership = false,
  isFreeAccount = true,
  membershipExpired = false,
  currentPackage,
  membershipExpiresAt
}) => {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Determine the condition and action based on membership and credit status
  const getCondition = () => {
    // Condition 1: Active membership + sufficient credits
    if (hasActiveMembership && availableCredits >= requiredCredits) {
      return {
        type: 'confirm',
        title: 'Ready to Generate Metadata',
        description: `You have ${availableCredits} credits available. This will use ${requiredCredits} credit${requiredCredits > 1 ? 's' : ''}.`,
        icon: CheckCircle,
        iconColor: 'text-green-600',
        buttonText: 'Confirm Upload',
        buttonAction: onConfirm,
        buttonVariant: 'default' as const,
        showMembershipInfo: true
      };
    }

    // Condition 2: Active membership + insufficient credits
    if (hasActiveMembership && availableCredits < requiredCredits) {
      return {
        type: 'buy_credits',
        title: 'Insufficient Credits',
        description: `You need ${requiredCredits} credits but only have ${availableCredits}. Purchase more credits to continue.`,
        icon: ShoppingCart,
        iconColor: 'text-orange-600',
        buttonText: 'Buy Credits',
        buttonAction: () => {
          window.location.href = '/pricing#topup';
        },
        buttonVariant: 'default' as const,
        showMembershipInfo: true
      };
    }

    // Condition 3: Free account + sufficient credits
    if (isFreeAccount && availableCredits >= requiredCredits) {
      return {
        type: 'free_confirm',
        title: 'Free Account - Limited Upload',
        description: `You have ${availableCredits} credits available. Free accounts are limited to 10 uploads per month.`,
        icon: AlertTriangle,
        iconColor: 'text-blue-600',
        buttonText: 'Confirm Upload',
        buttonAction: onConfirm,
        buttonVariant: 'outline' as const,
        showMembershipInfo: false
      };
    }

    // Condition 4: Expired membership + sufficient credits
    // CHANGED: Allow credit usage even if membership expired, as long as user has credits
    if (availableCredits >= requiredCredits) {
      return {
        type: 'confirm',
        title: 'Ready to Generate Metadata',
        description: `You have ${availableCredits} credits available. This will use ${requiredCredits} credit${requiredCredits > 1 ? 's' : ''}.`,
        icon: CheckCircle,
        iconColor: 'text-green-600',
        buttonText: 'Confirm Upload',
        buttonAction: onConfirm,
        buttonVariant: 'default' as const,
        showMembershipInfo: false
      };
    }

    // Fallback: No credits
    return {
      type: 'no_credits',
      title: 'No Credits Available',
      description: 'You need credits to generate metadata. Purchase top-up credits.',
      icon: AlertTriangle,
      iconColor: 'text-red-600',
      buttonText: 'Get Credits',
      buttonAction: () => {
        window.location.href = '/pricing#topup';
      },
      buttonVariant: 'default' as const,
      showMembershipInfo: false
    };
  };

  const condition = getCondition();
  const IconComponent = condition.icon;

  const formatMembershipExpiry = (expiresAt?: string) => {
    if (!expiresAt) return 'Unknown';
    const date = new Date(expiresAt);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center space-x-3 mb-6">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${condition.iconColor.replace('text-', 'bg-').replace('-600', '-100')}`}>
            <IconComponent className={`w-6 h-6 ${condition.iconColor}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {condition.title}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {condition.description}
            </p>
          </div>
        </div>

        {/* Credit Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-gray-600">Your Credits:</span>
            <span className="font-medium">{availableCredits}</span>
          </div>
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-gray-600">Required:</span>
            <span className="font-medium">{requiredCredits}</span>
          </div>
          <div className="flex justify-between items-center text-sm font-medium">
            <span>Remaining:</span>
            <span className={availableCredits >= requiredCredits ? 'text-green-600' : 'text-red-600'}>
              {availableCredits - requiredCredits}
            </span>
          </div>
        </div>

        {/* Membership Info */}
        {condition.showMembershipInfo && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-sm">
              <Crown className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-800">
                {currentPackage ? `${currentPackage} Membership` : 'Membership Status'}
              </span>
            </div>
            <div className="text-xs text-blue-600 mt-1">
              {hasActiveMembership ? (
                `Expires: ${formatMembershipExpiry(membershipExpiresAt)}`
              ) : (
                'Inactive or Expired'
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={condition.buttonAction}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
              condition.buttonVariant === 'default'
                ? 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                : 'text-blue-600 bg-white border border-blue-300 hover:bg-blue-50 focus:ring-blue-500'
            }`}
          >
            {condition.buttonText}
          </button>
        </div>

        {/* Additional Info */}
        {condition.type === 'free_confirm' && (
          <div className="text-xs text-gray-500 text-center mt-4">
            Upgrade to a membership for unlimited uploads and priority processing
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditValidationModal;